// What the client-facing PDF prints, and when it can be downloaded.
import {
  PDFReportClientInfoItem,
  PDFReportPerformanceChart,
  PDFReportRequest,
  PDFReportThemeInfoItem,
  ReportThemedCardConfig,
} from "helpers/types";
import { ChartSpec } from "../Charts/chartSeriesSelection";
import { buildOverlayChartData } from "../Charts/overlayChartData";
import { toPdfValues } from "../reportPdfUtils";
import { UNAVAILABLE_DISPLAY } from "../reportFieldStates";

/**
 * What a single-value figure exports as, given the sentence (if any) saying
 * the backend refused it.
 *
 * The screen and the paper have to agree, and they take different routes to
 * the same field: the card reads N/A off the reason, while the export takes
 * the stored figure. Without this substitution a client's PDF would print a
 * return the backend declined to stand behind -- on the one document that
 * leaves the building (operator, task 3239).
 *
 * The two characters travel and the sentence does not: a tooltip has no paper
 * equivalent, and "N/A" is an accepted export value for exactly this reason
 * (services/constants.py PDF_REPORT_PLACEHOLDER_VALUES, kept in step by hand
 * across the language boundary).
 */
export const pdfFigureValue = (
  stored: number | string,
  unavailableReason?: string,
): string => (unavailableReason ? UNAVAILABLE_DISPLAY : String(stored));

export const PDF_DOWNLOAD_FAILED_MESSAGE =
  "Failed to download PDF. Please try again.";

/**
 * Whether Download PDF can be pressed, and the hover hint saying why not
 * (operator, task 4562).
 *
 * A report is on screen when the Summary figures were CALCULATED FOR THE
 * INPUTS ON SCREEN: `reportInputsKey` is the key of the run that stored them,
 * passed only while that run's report is displayed -- "" otherwise, so
 * figures remembered from an earlier visit do not export behind the Calculate
 * prompt (task 5998), and ones from another account, period or Bucket 3 never
 * do -- they would print one client's numbers under another's name. Only a run
 * against the inputs on screen blocks it, the same scope that locks
 * Calculate (useInFlightCalculations): its figures are about to replace the
 * ones the PDF would print.
 */
export const downloadPdfAvailability = (
  reportInputsKey: string | number,
  currentInputsKey: string,
  isCalculatingTheseInputs: boolean,
): { disabled: boolean; hint: string } => {
  if (isCalculatingTheseInputs)
    return {
      disabled: true,
      hint: "Wait for the calculation to finish",
    };
  if (reportInputsKey !== currentInputsKey)
    return {
      disabled: true,
      hint: "Calculate the report to download the PDF",
    };
  return { disabled: false, hint: "" };
};

/**
 * The whole Strategy Report PDF request: the performance chart as it stands
 * on screen, the Summary tiles, and the four themed cards.
 */
export const buildStrategyPdfRequest = (
  chartSpecs: ChartSpec[],
  settingsInfo: PDFReportRequest["settings_info"],
  clientInfo: PDFReportClientInfoItem[],
  themedCards: ReportThemedCardConfig[],
): PDFReportRequest => {
  // The lines the reader has selected, aligned onto the chart's own date
  // axis. A series the backend refused is not sent -- the client's copy
  // carries drawn lines only (operator, task 4179).
  const { dates, lines } = buildOverlayChartData(chartSpecs);
  const performance_chart: PDFReportPerformanceChart = {
    dates,
    lines: lines.map(({ title, color, dash, totals }) => ({
      title,
      color,
      dash,
      totals,
    })),
  };

  const theme_info: PDFReportThemeInfoItem[] = themedCards.map((card) => ({
    label: card.label,
    description: card.description,
    // The export's own rows where a card has them -- today only the
    // Diversify card, whose stock switch does not travel to paper.
    values: toPdfValues(card.pdfValues ?? card.values),
    addictional_component: card.addictional_component,
  }));

  return {
    type: "strategy",
    settings_info: settingsInfo,
    performance_chart,
    client_info: clientInfo,
    theme_info,
  };
};
